import { forwardRef } from 'react'

import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

const avatarVariants = tv({
  slots: {
    root: 'font-body relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold',
    image: 'size-full object-cover',
    fallback:
      'flex size-full items-center justify-center uppercase leading-none'
  },
  variants: {
    size: {
      xs: { root: 'size-6 text-[0.625rem]' },
      sm: { root: 'size-8 text-xs' },
      md: { root: 'size-10 text-sm' },
      lg: { root: 'size-12 text-base' }
    },
    color: {
      primary: { root: 'bg-primary-200 text-white' },
      neutral: { root: 'bg-surface-200 text-surface-title-50' }
    }
  },
  defaultVariants: { size: 'md', color: 'primary' }
})

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

export type AvatarProps = Omit<
  React.ComponentPropsWithRef<typeof AvatarPrimitive.Root>,
  'color'
> &
  VariantProps<typeof avatarVariants> & {
    name?: string
    src?: string
    alt?: string
  }

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ name, src, alt, size, color, className, children, ...props }, ref) => {
    const styles = avatarVariants({ size, color })
    const fallback = children ?? (name ? getInitials(name) : null)

    return (
      <AvatarPrimitive.Root
        ref={ref}
        data-slot='avatar'
        className={cn(styles.root(), className)}
        {...props}
      >
        {src ? (
          <AvatarPrimitive.Image
            src={src}
            alt={alt ?? name ?? ''}
            className={styles.image()}
          />
        ) : null}
        <AvatarPrimitive.Fallback className={styles.fallback()}>
          {fallback}
        </AvatarPrimitive.Fallback>
      </AvatarPrimitive.Root>
    )
  }
)
Avatar.displayName = 'Avatar'
